import React, { useState, MouseEvent, useRef, CSSProperties } from 'react';
import { buildClassName } from 'utils/build-class-name';
import { LOGIN_REGISTRATION_MODAL, useModalContext, useSavedListingContext, useThemeContext, useUserContext } from 'contexts';
import HollowHeartIcon from 'components/icon/hollow-heart-icon';
import styles from './style.module.scss';
import { dynamicTestIds, listingCardIds } from 'constants/test-constants';
import { AnalyticsController } from 'analytics/analytics-controller';
import { GTM_LISTING_CARD_FAVOURITE_TOGGLE_CLICK } from 'constants/events';
import { isExpTenant } from '@tenants/utils/which-tenant';

import type { PropertyFavouritedSource } from '@bedrock/analytics';

const SET_IS_LOADING_DELAY = 500 as const;

const BURST_DOTS = [
  { x: -15, y: -20, delay: 0 },
  { x: 0, y: -26, delay: 0.02 },
  { x: 16, y: -18, delay: 0.04 },
  { x: -20, y: -4, delay: 0.06 },
  { x: 20, y: 0, delay: 0.08 },
] as const;
const BURST_STAGGER = 1.25;
const BURST_DOT_DURATION_MS = 525;
const LAST_BURST_DOT_DELAY_MS = Math.round(BURST_DOTS[BURST_DOTS.length - 1].delay * BURST_STAGGER * 1000);
export const BURST_DURATION_MS = BURST_DOT_DURATION_MS + LAST_BURST_DOT_DELAY_MS;

type BurstDotStyle = CSSProperties & { '--burst-x': string; '--burst-y': string };

function getBurstDotStyle({ x, y, delay }: typeof BURST_DOTS[number]): BurstDotStyle {
  return {
    '--burst-x': `${x}px`,
    '--burst-y': `${y}px`,
    animationDelay: `${delay * BURST_STAGGER}s`,
  };
}

/**
 * `default` swaps the heart for a spinner while saving.
 * `burst` fills the heart at once and plays a short burst instead
 */
export type FavouriteToggleVariant = 'default' | 'burst';

type AnalyticsProps = {
  analytics?: {
    /** Function to track the favourite event. */
    favouriteEventTracker?: () => void;
    /** Source for property_favourited analytics (e.g. ADDRESS_PAGE, MAP_GRID). */
    source?: PropertyFavouritedSource;
  };
};

type FavouriteToggleProps = {
  listingId: number;
  addressSlug?: string;
  isInCarousel?: boolean;
  isSmallCard?: boolean;
  isModal?: boolean;
  isHeartExperiment?: boolean;
  variant?: FavouriteToggleVariant;
} & AnalyticsProps;

type FavouriteToggleBaseProps = {
  toggleFavouriteState: () => void;
  isFavourite: boolean;
  isInCarousel?: boolean;
  isSmallCard?: boolean;
  isLoading?: boolean;
  variant?: FavouriteToggleVariant;
  isBursting?: boolean;
};

export const FavouriteToggleBase = ({ isInCarousel, isSmallCard, isFavourite, toggleFavouriteState, isLoading, variant = 'default', isBursting = false }: FavouriteToggleBaseProps) => {
  const isBurstVariant = variant === 'burst';
  const showBurst = isBurstVariant && isBursting;
  const containerClassName = buildClassName(
    isInCarousel && styles.component,
    isSmallCard && styles['small-card'],
    styles['favourite-wrapper'],
    isInCarousel && styles['address-page-icon']
  );
  const dynamicTestid = isFavourite ? dynamicTestIds.favourite : dynamicTestIds.notFavourite;

  const handleOnCLick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    toggleFavouriteState?.();
  };
  const { tenantId } = useThemeContext();
  const isExpTheme = isExpTenant(tenantId);

  return (
    <div className={containerClassName} onClick={handleOnCLick} data-testid={listingCardIds.favouriteToggle} data-dynamic-testid={dynamicTestid}>
      {isLoading && !isBurstVariant ? (
        <div className={styles.spinnerWrapper}>
          <div className={styles.spinner} />
        </div>
      ) : isBurstVariant ? (
        <span className={buildClassName(styles.heartLayer, showBurst && styles.heartPop)}>
          <HollowHeartIcon
            className={buildClassName(styles.favourite, isFavourite && styles.active, isExpTheme && styles.exp, styles.burstVariant)}
            onClick={handleOnCLick}
          />
        </span>
      ) : (
        <HollowHeartIcon
          className={buildClassName(styles.favourite, isFavourite && styles.active, isExpTheme && styles.exp)}
          onClick={handleOnCLick}
        />
      )}
      {showBurst && (
        <span className={styles.burst} aria-hidden data-testid={listingCardIds.favouriteBurst}>
          <span className={styles.burstRing} />
          {BURST_DOTS.map(dot => (
            <span key={`${dot.x},${dot.y}`} className={styles.burstDot} style={getBurstDotStyle(dot)} />
          ))}
        </span>
      )}
    </div>
  );
};

export default function FavouriteToggle({ listingId, addressSlug, isInCarousel, isSmallCard, isModal = false, isHeartExperiment = false, variant = 'default', analytics: { favouriteEventTracker, source } = {}}: FavouriteToggleProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isBursting, setIsBursting] = useState(false);
  const playBurst = () => {
    if (variant !== 'burst') return;
    setIsBursting(true);
    setTimeout(() => setIsBursting(false), BURST_DURATION_MS);
  };
  // This is separate from `isLoading`, which only controls the short spinner.
  // `isPending` stays true until the request is complete, so a second click is
  // ignored while the first request is still running.
  const [isPending, setIsPending] = useState(false);
  const showsSpinner = variant !== 'burst';

  const runFavouriteRequest = (request: Promise<void> | void) => {
    setIsPending(true);
    // `Promise.resolve` is used so that a caller or a test stub which returns
    // nothing still clears `isPending`, instead of throwing.
    //
    // There is deliberately no check for whether the component is still
    // mounted. On React 18, setting state after unmount does nothing. A check
    // stored in a ref is worse than no check: in StrictMode the extra unmount
    // runs the cleanup that sets the ref to false, and it is never set back to
    // true, so `isPending` is never cleared again.
    void Promise.resolve(request).finally(() => setIsPending(false));
  };
  const { openModal } = useModalContext();
  const { isAuthenticated } = useUserContext();
  const { addSavedListing, removeSavedListing, isSavedListing } = useSavedListingContext();
  const hasBeenClicked = useRef(false);
  const isFavourite = isSavedListing(addressSlug, Number(listingId));

  const toggleFavouriteState = () => {
    if (isLoading || isPending) return;

    if (!hasBeenClicked.current && isHeartExperiment) {
      AnalyticsController.trackEvent(GTM_LISTING_CARD_FAVOURITE_TOGGLE_CLICK);
      hasBeenClicked.current = true;
    }

    if (isAuthenticated) {
      if (showsSpinner) setIsLoading(true);

      if (isFavourite) {
        runFavouriteRequest(removeSavedListing(listingId, source !== undefined ? { source } : undefined));
        if (showsSpinner) setTimeout(() => setIsLoading(() => false), SET_IS_LOADING_DELAY);
      } else {
        runFavouriteRequest(addSavedListing({ listingId, isModal, ...(source !== undefined && { source }) }));
        if (showsSpinner) setTimeout(() => setIsLoading(() => false), SET_IS_LOADING_DELAY);
        playBurst();
        favouriteEventTracker?.();
      }
    } else {
      openModal(LOGIN_REGISTRATION_MODAL, {
        callback: () => {
          runFavouriteRequest(addSavedListing({ listingId, isModal, ...(source !== undefined && { source }) }));
          if (showsSpinner) setTimeout(() => setIsLoading(() => false), SET_IS_LOADING_DELAY);
          playBurst();
        },
      });
    }
  };

  return (
    <>
      <FavouriteToggleBase
        isFavourite={isFavourite}
        isInCarousel={isInCarousel}
        isSmallCard={isSmallCard}
        toggleFavouriteState={toggleFavouriteState}
        isLoading={isLoading}
        variant={variant}
        isBursting={isBursting}
      />
    </>
  );
}
